import type { Quote } from '../types';

/**
 * Escape a value for CSV, handling special characters and CSV injection
 */
export function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);

  // Prevent CSV injection (formulas starting with =, +, -, @, tab, or carriage return)
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  // If value contains comma, quote, or newline, wrap in quotes and escape inner quotes
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Format a date string for CSV export
 */
function formatDateForCSV(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-CA'); // YYYY-MM-DD format
  } catch {
    return dateStr;
  }
}

/**
 * CSV column definitions
 */
const COLUMNS: Array<{
  key: string;
  label: string;
  getValue: (quote: Quote) => string | number;
}> = [
  { key: 'quoteNumber', label: 'Quote Number', getValue: (q) => q.quoteNumber },
  { key: 'status', label: 'Status', getValue: (q) => q.status },
  { key: 'accountName', label: 'Account', getValue: (q) => q.accountName || '' },
  { key: 'projectName', label: 'Project Name', getValue: (q) => q.projectName || '' },
  { key: 'salesManagerName', label: 'Account Manager', getValue: (q) => q.salesManagerName || '' },
  { key: 'contractualManagerName', label: 'Contact', getValue: (q) => q.contractualManagerName || '' },
  { key: 'total', label: 'Total', getValue: (q) => parseFloat(q.total) || 0 },
  { key: 'currency', label: 'Currency', getValue: (q) => q.currency },
  { key: 'createdAt', label: 'Created Date', getValue: (q) => formatDateForCSV(q.createdAt) },
  { key: 'expirationDate', label: 'Expiration Date', getValue: (q) => formatDateForCSV(q.expirationDate) },
  { key: 'acceptedDate', label: 'Accepted Date', getValue: (q) => formatDateForCSV(q.acceptedDate) },
  { key: 'hubspotLink', label: 'HubSpot Link', getValue: (q) => q.hubspotLink || '' },
  { key: 'gdriveLink', label: 'Google Drive Link', getValue: (q) => q.gdriveLink || '' },
];

/**
 * Export quotes to CSV file and trigger download
 */
export function exportQuotesToCSV(quotes: Quote[], filename = 'quotes'): void {
  // Build header row
  const header = COLUMNS.map((c) => escapeCSVValue(c.label)).join(',');

  // Build data rows
  const rows = quotes.map((quote) =>
    COLUMNS.map((c) => escapeCSVValue(c.getValue(quote))).join(',')
  );

  // Combine into CSV content
  const csvContent = [header, ...rows].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
